import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Collapse,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import axios from "axios";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import GroupIcon from "@mui/icons-material/Group";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import NomineeDialog from "./NomineeDialog";
import NomineeCard from "./NomineeCard";
import { API_BASE_URL, PRIMARY } from "./shared";

// ─── NomineesSection ──────────────────────────────────────────────────────────
// Shown below Documents. Handles both create-mode (local state) and edit-mode
// (API calls with immediate feedback).
const NomineesSection = ({
  mode, // 'create' | 'edit'
  customerId,
  customerName,
  canAddNominee = true, // false until a customer is created/selected
  // Stretch to fill the height handed down by the parent (the Customer Master's
  // one-page layout) and scroll the cards inside instead of growing the page.
  fill = false,
  storeId,
  onNomineeUpdated,
  localNominees,
  onLocalNomineesChange,
  // edit mode: nominees fetched from server and refreshed after each operation
  serverNominees,
  onRefresh,
  showSnack,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitialData, setDialogInitialData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [nomineeToRemove, setNomineeToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  // Active-loan pre-check state for the remove dialog.
  const [isCheckingLoans, setIsCheckingLoans] = useState(false);
  // Non-null ⇒ removal is blocked; holds the message to display.
  const [nomineeBlockInfo, setNomineeBlockInfo] = useState(null);

  const nominees = mode === "create" ? localNominees : serverNominees;

  const handleAddClick = () => {
    if (!canAddNominee) {
      showSnack(
        "Please enter the customer's name and mobile before adding a nominee.",
        "warning",
      );
      return;
    }
    setDialogInitialData(null);
    setDialogOpen(true);
  };

  const handleEditClick = (nominee, index) => {
    const fresh = nominees.find((n) =>
      nominee.link_id ? n.link_id === nominee.link_id : n === nominee,
    );
    setDialogInitialData(fresh ?? nominee);
    setDialogOpen(true);
  };

  const handleRemoveClick = async (nominee, index) => {
    setNomineeToRemove({ nominee, index });
    setNomineeBlockInfo(null);
    setRemoveDialogOpen(true);

    // Create-mode nominees aren't saved yet, so they can't have loans.
    if (mode !== "edit" || !nominee.nominee_id) return;

    setIsCheckingLoans(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/${nominee.nominee_id}/active-loans`,
        { params: { store_id: storeId } },
      );
      if (res.data.success && res.data.data?.blocked) {
        const total = res.data.data.totalActive;
        setNomineeBlockInfo({
          message: `This nominee is selected for ${total} active loan(s). Cannot proceed for deletion.`,
        });
      }
    } catch (error) {
      // If the pre-check fails, fall through to the confirm dialog — the update
      // endpoint still guards against removing a loan-linked nominee.
      console.error("Error checking nominee active loans:", error);
    } finally {
      setIsCheckingLoans(false);
    }
  };

  const confirmRemove = async () => {
    if (!nomineeToRemove) return;
    const { nominee, index } = nomineeToRemove;

    if (mode === "create") {
      // Just splice from local array
      const updated = localNominees.filter((_, i) => i !== index);
      onLocalNomineesChange(updated);
      setRemoveDialogOpen(false);
      setNomineeToRemove(null);
      showSnack("Nominee removed", "info");
      return;
    }

    // Edit mode: fire API
    if (!nominee.link_id) {
      showSnack("Cannot remove — nominee has no link ID", "error");
      setRemoveDialogOpen(false);
      return;
    }
    setIsRemoving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/pawn/customers/${customerId}`,
        {
          store_id: storeId,
          nominees_to_remove: [nominee.link_id],
        },
      );
      if (!res.data.success) throw res.data;
      showSnack("Nominee removed successfully", "success");
      onRefresh();
    } catch (error) {
      showSnack(error?.statusMessage || "Failed to remove nominee", "error");
    } finally {
      setIsRemoving(false);
      setRemoveDialogOpen(false);
      setNomineeToRemove(null);
    }
  };

  // Called by NomineeDialog in create mode
  const handleLocalSave = (nomineeData) => {
    let updated;
    if (dialogInitialData !== null) {
      // Editing an existing local nominee
      const idx = localNominees.indexOf(dialogInitialData);
      updated = [...localNominees];
      if (idx !== -1) updated[idx] = nomineeData;
      else updated.push(nomineeData);
    } else {
      updated = [...localNominees, nomineeData];
    }

    // Enforce a single primary nominee: if the saved one is primary, demote
    // every other local nominee.
    let demoted = false;
    if (nomineeData.is_primary) {
      updated = updated.map((n) => {
        if (n === nomineeData) return n;
        if (n.is_primary) {
          demoted = true;
          return {
            ...n,
            is_primary: 0,
            _snapshot: n._snapshot
              ? { ...n._snapshot, isPrimary: false }
              : n._snapshot,
          };
        }
        return n;
      });
    }

    onLocalNomineesChange(updated);
    showSnack(
      demoted
        ? "Primary nominee updated — only one primary is allowed"
        : "Nominee added — will be saved with the customer",
      "info",
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 2,
        // Only from md up: below that the panels are stacked and the page is
        // allowed to scroll, so the card keeps its natural height instead of
        // being squeezed to its min-content size.
        ...(fill && {
          flex: { xs: "0 0 auto", md: 1 },
          minHeight: { xs: 0, md: 170 },
          display: "flex",
          flexDirection: "column",
          overflow: { xs: "visible", md: "hidden" },
        }),
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: expanded ? 2 : 0,
          cursor: "pointer",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GroupIcon sx={{ color: PRIMARY }} />
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: PRIMARY }}
          >
            Nominees
          </Typography>
          {nominees.length > 0 && (
            <Badge
              badgeContent={nominees.length}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: PRIMARY,
                  color: "#fff",
                  fontSize: "0.65rem",
                  minWidth: 18,
                  height: 18,
                },
              }}
            >
              <Box sx={{ width: 8 }} />
            </Badge>
          )}
          {mode === "create" && nominees.length > 0 && (
            <Chip
              label="Saved with customer"
              size="small"
              sx={{
                fontSize: "0.65rem",
                height: 20,
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                display: { xs: "none", sm: "inline-flex" },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip
            title={
              canAddNominee
                ? ""
                : "Enter the customer's name and mobile first"
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={!canAddNominee}
                startIcon={<PersonAddRoundedIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddClick();
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  backgroundColor: PRIMARY,
                  fontSize: "0.75rem",
                  py: 0.5,
                  "&:hover": { backgroundColor: PRIMARY },
                }}
              >
                Add Nominee
              </Button>
            </span>
          </Tooltip>
          <IconButton size="small" sx={{ color: PRIMARY }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse
        in={expanded}
        sx={
          fill
            ? {
                flex: { xs: "0 0 auto", md: 1 },
                minHeight: 0,
                overflowY: { xs: "visible", md: "auto" },
                "&::-webkit-scrollbar": { width: 0, height: 0 },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
                  height: "100%",
                },
              }
            : undefined
        }
      >
        {nominees.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              border: "1.5px dashed #e0e0e0",
              borderRadius: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <GroupIcon sx={{ fontSize: 36, color: "#bdbdbd", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No nominees added yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {canAddNominee
                ? 'Tap "Add Nominee" to add one'
                : "Enter the customer's name and mobile first to add a nominee"}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {nominees.map((nominee, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <NomineeCard
                  nominee={nominee}
                  index={index}
                  mode={mode}
                  onEdit={handleEditClick}
                  onRemove={handleRemoveClick}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Collapse>

      {/* Nominee dialog */}
      <NomineeDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setDialogInitialData(null);
        }}
        mode={mode}
        initialData={dialogInitialData}
        customerId={customerId}
        customerName={customerName}
        siblingNominees={nominees}
        storeId={storeId}
        onSaved={onRefresh}
        onNomineeUpdated={onNomineeUpdated}
        onLocalSave={handleLocalSave}
        showSnack={showSnack}
      />

      {/* Remove confirmation */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        {isCheckingLoans ? (
          <>
            <DialogTitle>Please wait</DialogTitle>
            <DialogContent
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}
            >
              <CircularProgress size={22} />
              <span>Checking for active loans…</span>
            </DialogContent>
          </>
        ) : nomineeBlockInfo ? (
          <>
            <DialogTitle
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <WarningAmberIcon color="warning" />
              Cannot Remove Nominee
            </DialogTitle>
            <DialogContent>
              <DialogContentText>{nomineeBlockInfo.message}</DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button
                variant="contained"
                onClick={() => setRemoveDialogOpen(false)}
                autoFocus
              >
                Understood
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <WarningAmberIcon color="warning" />
              Remove Nominee
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to remove{" "}
                <strong>
                  {nomineeToRemove?.nominee?.name ||
                    nomineeToRemove?.nominee?._snapshot?.name}
                </strong>{" "}
                as a nominee?
                {mode === "edit" && (
                  <> This will immediately update the record.</>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button
                onClick={() => setRemoveDialogOpen(false)}
                disabled={isRemoving}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={confirmRemove}
                disabled={isRemoving}
                startIcon={
                  isRemoving ? <CircularProgress size={14} /> : undefined
                }
              >
                Remove
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default NomineesSection;
